import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { FaArrowLeft } from 'react-icons/fa6';

import SedeList from './SedeList.component';
import { SedeMutedLine } from './SedeList.style';
import { SedeSection, BackButton } from './SedeSwitcher.style';
import usePartnerDirectory from '../../../hooks/usePartnerDirectory';
import { isPartnerAccessEnabled } from '../../../utils/partnerAccess/partnerFlag';
import { getActiveSedeId } from '../../../utils/partnerAccess/partnerStorage';
import {
  openSedeInNewTab,
  returnToOwnCompany,
  switchToSede,
} from '../../../utils/partnerAccess/sedeSession';

/**
 * The sedes a partner can work, inside the profile dropdown. It renders
 * nothing (and asks for nothing) for anyone who is not a partner. A sede tab
 * always gets the way back to the own account, with or without the sedes.
 */
const SedeSwitcher = ({
  open,
  userId,
  firstBlock,
  onOverview,
  onPartnerChange,
  searchFocus,
}) => {
  const { directory, summary, unavailable } = usePartnerDirectory({ open, userId });

  const isPartner = directory !== null;
  useEffect(() => {
    if (onPartnerChange) onPartnerChange(isPartner);
  }, [isPartner]);

  const activeSedeId = getActiveSedeId();
  // Leaving a sede lands on the overview of all of them; without it (the
  // menu was not given one), on the partner's own account. The button says
  // which of the two it is.
  const leaveSede = () => (onOverview ? onOverview() : returnToOwnCompany());
  const backButton = activeSedeId && (
    <BackButton type="button" onClick={leaveSede}>
      <FaArrowLeft aria-hidden="true" />
      {onOverview ? 'Volver a la vista general' : 'Volver a mi cuenta'}
    </BackButton>
  );

  if (!directory) {
    // Leaving a sede must not depend on the directory: it can be loading, or
    // out of reach (rate limit, outage, a tab opened with no cache).
    if (!activeSedeId || !isPartnerAccessEnabled()) return null;
    return (
      <SedeSection aria-label="Sedes" firstBlock={firstBlock}>
        {backButton}
        {unavailable && <SedeMutedLine>No se pudieron cargar las sedes</SedeMutedLine>}
      </SedeSection>
    );
  }

  return (
    <SedeSection aria-label="Sedes" firstBlock={firstBlock}>
      <SedeList
        groupName={directory.groupName}
        sedes={directory.sedes}
        summary={summary}
        activeSedeId={activeSedeId}
        onSelect={switchToSede}
        onOpenInNewTab={openSedeInNewTab}
        afterHeader={backButton}
        onOverview={onOverview}
        searchFocus={searchFocus}
        showSearchShortcut
      />
    </SedeSection>
  );
};

SedeSwitcher.propTypes = {
  open: PropTypes.bool.isRequired,
  userId: PropTypes.string,
  // Nothing comes before it in the dropdown: no divider over the header.
  firstBlock: PropTypes.bool,
  // Opens the overview of all the sedes; without it the card is not offered.
  onOverview: PropTypes.func,
  // Told whether the sedes of a partner are on screen, each time it changes.
  onPartnerChange: PropTypes.func,
  // The search takes the focus each time this changes to a truthy value.
  searchFocus: PropTypes.number,
};

SedeSwitcher.defaultProps = {
  userId: undefined,
  firstBlock: false,
  onOverview: undefined,
  onPartnerChange: undefined,
  searchFocus: 0,
};

export default SedeSwitcher;
